/**
 * Iter-27.47 — AudioManager global anti-chevauchement.
 *
 * Singleton qui garantit qu'un seul `Audio` joue à la fois dans toute l'app.
 * - Le PodcastContext et LessonPresentation s'enregistrent tous deux ici
 * - Quand l'un prend la main (`play`), l'autre est automatiquement stoppé
 * - Évite les race conditions entre fetch + play sur changement rapide de slide/leçon
 *
 * Usage :
 *   import audioManager from "@/lib/audioManager";
 *   const handle = audioManager.acquire("lesson-presentation");  // identifiant source
 *   const audio = new Audio(url);
 *   handle.attach(audio);  // remplace tout audio précédent + s'enregistre
 *   await audio.play();
 *   // Quand une autre source acquiert, audio est automatiquement pausé
 */

class AudioManager {
    constructor() {
        this.activeAudio = null;
        this.activeOwner = null;
        this.listeners = new Set();
    }

    /**
     * Acquiert le control. Retourne un handle avec attach()/release().
     * Si une autre source jouait, son audio est pausé proprement.
     */
    acquire(ownerId) {
        // Stoppe l'audio actuel si une autre source prend la main
        if (this.activeOwner && this.activeOwner !== ownerId && this.activeAudio) {
            try {
                this.activeAudio.pause();
                this.activeAudio.src = "";
                this.activeAudio.load(); // force le navigateur à libérer le buffer
            } catch {
                /* */
            }
            this._notify({ type: "stopped-by-other", ownerId: this.activeOwner });
            this.activeAudio = null;
            this.activeOwner = null;
        }

        return {
            attach: (audio) => {
                // Si une autre source avait le control, on l'a déjà clean ci-dessus.
                // Si la même source rejoue : on stoppe l'ancienne instance avant.
                if (this.activeAudio && this.activeAudio !== audio) {
                    try {
                        this.activeAudio.pause();
                        this.activeAudio.src = "";
                    } catch {
                        /* */
                    }
                }
                this.activeAudio = audio;
                this.activeOwner = ownerId;
            },
            release: () => {
                if (this.activeOwner === ownerId) {
                    if (this.activeAudio) {
                        try {
                            this.activeAudio.pause();
                        } catch {
                            /* */
                        }
                    }
                    this.activeAudio = null;
                    this.activeOwner = null;
                }
            },
            isActive: () => this.activeOwner === ownerId,
        };
    }

    /** Stoppe TOUT audio actif (pour cleanup global, ex: logout, navigation). */
    stopAll() {
        if (this.activeAudio) {
            try {
                this.activeAudio.pause();
                this.activeAudio.src = "";
            } catch {
                /* */
            }
            this.activeAudio = null;
            this.activeOwner = null;
        }
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    _notify(event) {
        for (const l of this.listeners) {
            try { l(event); } catch { /* */ }
        }
    }
}

const audioManager = new AudioManager();
export default audioManager;
